import React, { useContext, useRef, useEffect } from 'react';
import { GoogleMap, MarkerF, PolygonF, PolylineF } from '@react-google-maps/api';
import { FormContext } from 'context/FormContext';
import { useGoogleMaps } from 'context/GoogleContext';
import * as turf from '@turf/turf'
import IconMapMarker from 'assets/images/icon_map_marker.svg';
import IconMapMarkerLast from 'assets/images/icon_map_marker_last.svg';

const MapRoofSelection = ({ selectedZone }) => {
    const { formValues, setFormValues } = useContext(FormContext);
    const { google } = useGoogleMaps();
    const polygonRefs = useRef([]);
    const polylineRef = useRef();

    const mapStyles = {
        width: '100%',
        height: '100%',
    };

    useEffect(() => {
        formValues.Roof.Zones.forEach((zone, zIndex) => {
            const polygon = polygonRefs.current[zIndex];
            if (polygon) {
                polygon.setOptions({
                    fillOpacity: zIndex === selectedZone ? 0.35 : 0.5,
                    fillColor: zIndex === selectedZone ? "#5CC244" : "black",
                });
            }
        });
    }, [selectedZone, formValues.Roof.Zones]);

    // Doesnt work...
    // useEffect(() => {
    //     console.log('useEffect', formValues.Roof.Zones.length);
    //     if (formValues.Roof.Zones.length > 0) {
    //         console.log('useEffect', formValues.Roof.Zones.length);
    //          setFormValues(prev => ({
    //         ...prev,
    //         SkipRoof: false
    //         }));
    //     }
    // }, [formValues.Roof.Zones]);

    const updatePolygons = () => {
        formValues.Roof.Zones.forEach((zone, index) => {
            if (polygonRefs.current[index]) {
                polygonRefs.current[index].setOptions({
                    paths: formValues.Roof.Zones[index].Points,
                });
                const squareMeter = google.maps.geometry.spherical.computeArea(formValues.Roof.Zones[index].Points);
                formValues.Roof.Zones[index].SquareMeter = squareMeter;
            }
        });
        formValues.SkipRoof = false;
        setFormValues({ ...formValues });
        updateSquareMeters();
    };

    const updateSquareMeters = () => {
        const newValues = { ...formValues };
        let totalSquareMeter = 0;
        newValues.Roof.Zones.forEach((zone, zindex) => {
            totalSquareMeter = zone.SquareMeter;
            newValues.Roof.Obstacles.forEach((obstacle, oindex) => {
                const zoneArea = convertGMapsPolygonToGEOJSON(zone.Points)
                const obstacleArea = convertGMapsPolygonToGEOJSON(obstacle.Points)
                if (zoneArea.length >= 3 && obstacleArea.length >= 3) {
                    const zonePolygon = turf.polygon([zoneArea]);
                    const obstaclePolygon = turf.polygon([obstacleArea]);
                    const intersect = turf.intersect(zonePolygon, obstaclePolygon);
                    if (intersect) {
                        totalSquareMeter = totalSquareMeter - turf.area(intersect);
                        totalSquareMeter = Math.max(0, totalSquareMeter);
                    }
                }
            });
            // console.log('Zone', zindex, zone.SquareMeter, totalSquareMeter)
            // newValues.Roof.Zones[zindex] = { ...newValues.Roof.Zones[zindex], SquareMeterUsable: parseFloat((totalSquareMeter * -1).toFixed(2)) };
            newValues.Roof.Zones[zindex] = { ...newValues.Roof.Zones[zindex], SquareMeterUsable: parseFloat((totalSquareMeter).toFixed(2)) };
        });
        formValues.SkipRoof = false;
        setFormValues(newValues);
    };

    function convertGMapsPolygonToGEOJSON(googleMapsPolygon) {
        const coordinates = googleMapsPolygon
            .filter((latLng) => typeof latLng.lng === "number" && typeof latLng.lat === "number")
            .map(latLng => [latLng.lng, latLng.lat]);
        if (coordinates.length >= 3) {
            const first = coordinates[0];
            const last = coordinates[coordinates.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) {
                coordinates.push(first);
            }
        }
        return coordinates;
    }

    const mapOnClick = (e) => {
        const newValues = { ...formValues };
        if (!newValues.Roof.Zones[selectedZone]) {
            newValues.Roof.Zones[selectedZone] = {
                Points: [],
                SquareMeter: 0,
                Direction: 0,
                Tilt: 0
            }
        }
        newValues.Roof.Zones[selectedZone].Points.push({
            lat: e.latLng.lat(), lng: e.latLng.lng()
        });
        newValues.SkipRoof = false;
        setFormValues(newValues);
        updatePolygons();
    };

    const drawPolyline = (e) => {
        if (formValues.Roof.Zones[selectedZone] && polylineRef.current) {
            const zonePoints = formValues.Roof.Zones[selectedZone].Points;
            if (zonePoints.length) {
                const newZonePoints = [zonePoints[zonePoints.length - 1], { lat: e.latLng.lat(), lng: e.latLng.lng() }];
                const distance = google.maps.geometry.spherical.computeDistanceBetween(newZonePoints[0], newZonePoints[1]);
                if (distance > 20) {
                    polylineRef.current.setPath([]);
                    return;
                }
                polylineRef.current.setPath(newZonePoints);
            }
        }
    };

    const resetPolyline = () => {
        if (polylineRef.current) {
            polylineRef.current.setPath([]);
        }
    };

    const defaultCenter = { lat: 50.14407031082436, lng: 8.579974394470517 };
    const initialCenter = formValues.Address?.LatLng && formValues.Address.LatLng.lat && formValues.Address.LatLng.lng
        ? formValues.Address.LatLng
        : defaultCenter;

    return (
        <div className="MapContainer full-height map-container-extra">
            <GoogleMap
                mapContainerStyle={mapStyles}
                center={initialCenter}
                zoom={20}
                onLoad={(map) => {
                    map.mapTypeId = google.maps.MapTypeId.SATELLITE;
                    map.setTilt(0);
                }}
                onClick={mapOnClick}
                onMouseMove={drawPolyline}
                onMouseOut={resetPolyline}
                options={{
                    disableDefaultUI: true,
                    draggable: true
                }}
            >
                {formValues.Roof.Zones[selectedZone]?.Points.map((point, index) => (
                    <MarkerF
                        // title={`entry: ${formValues.Roof.Zones[selectedZone].Points.length}:${index + 1}`}
                        title={`Punkt ${index + 1}`}
                        name={index}
                        position={point}
                        key={`${selectedZone}-${index}`}
                        onClick={() => {
                            const newValues = { ...formValues };
                            newValues.Roof.Zones[selectedZone].Points.splice(index, 1);
                            newValues.SkipRoof = false;
                            setFormValues(newValues);
                            updatePolygons();
                        }}
                        draggable={true}
                        onLoad={(marker) => {
                            // can't use onDragEnd as state is not handled properly
                            google.maps.event.clearInstanceListeners(marker);
                            marker.addListener('dragend', (e) => {
                                // console.log('onDragEnd');
                                const newValues = { ...formValues };
                                newValues.Roof.Zones[selectedZone].Points[index] = {
                                    lat: e.latLng.lat(),
                                    lng: e.latLng.lng(),
                                    t: 2
                                };
                                newValues.SkipRoof = false;
                                setFormValues(newValues);
                                updatePolygons();
                            });
                        }}
                        // onDragEnd={(e) => {
                        //     const newValues = { ...formValues };
                        //     newValues.Roof.Zones[selectedZone].Points[index] = { lat: e.latLng.lat(), lng: e.latLng.lng(), t: 1};
                        //     setFormValues(newValues);
                        //     updatePolygons();
                        // }}
                        icon={{
                            url: formValues.Roof.Zones[selectedZone].Points.length > index + 1 ? IconMapMarker : IconMapMarkerLast,
                            anchor: new google.maps.Point(10, 10),
                            scaledSize: new google.maps.Size(20, 20),
                        }}
                    />
                ))}
                {formValues.Roof.Zones.map((zone, zIndex) => {
                    // polygonF throws error if paths is empty...
                    if (zone.Points.length > 0) {
                        return (
                            <PolygonF
                                paths={zone.Points}
                                key={zIndex}
                                onLoad={(polygon) => {
                                    polygonRefs.current[zIndex] = polygon;
                                    polygon.setOptions({
                                        strokeColor: 'green',
                                        strokeOpacity: 0.7,
                                        strokeWeight: 2,
                                        fillOpacity: zIndex === selectedZone ? 0.35 : 0.5,
                                        fillColor: zIndex === selectedZone ? "#5CC244" : "black",
                                        clickable: false,
                                    });
                                }}
                            />
                        );
                    }
                    return null;
                })}

                <PolylineF
                    path={[]}
                    onLoad={(polyline) => {
                        polylineRef.current = polyline;
                        polyline.setOptions({
                            strokeColor: 'green',
                            strokeOpacity: 1,
                            strokeWeight: 2,
                            clickable: false,
                        })
                    }}
                />
            </GoogleMap>
        </div>
    );
};

export default MapRoofSelection;
